import { collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from './firebase';
import { isSubtypeOf } from '../constants/productTaxonomy';
import { searchKey } from '../utils/searchKey';

// ── PRODUCT SEARCH ───────────────────────────────────────────────────────────
// Listings across every shop, for the Products tab of search.
//
// ── WHY THIS ONE IS NOT PURELY CLIENT-SIDE ──────────────────────────────────
// The designer and shop directories are fetched whole and filtered in memory
// (see designerService), because a directory row is a few hundred bytes. A
// product is not: Storage is not provisioned, so each metadata document carries
// its thumbnail inline as a data URL at roughly 30KB. Fetching a thousand of
// them to filter four fields would be a thirty-megabyte search box.
//
// So the TYPE is pushed to the server and everything else stays here. Type is
// the right axis to split on: it is the first thing anyone picks, it cuts the
// catalogue by an order of magnitude, and one equality filter needs one
// composite index rather than one per combination. Subtype, colour, price and
// free text then refine a list that is already small.
//
// Without a type the query falls back to the most recent listings, which is
// what an empty search should show anyway.
//
// REQUIRES a composite index on photos (visible, type, createdAt desc) — see
// firestore.indexes.json. Firestore refuses the query without it and prints a
// link to create it.

const POOL = 60;                    // per type; see the note above on size
const CACHE_TTL_MS = 5 * 60 * 1000;

// Keyed by type ('' for the recent-listings fallback), so switching between two
// types and back costs nothing.
const _cache = new Map();           // typeKey -> { rows, at }
const _inflight = new Map();

/** users-facing row shape, flattened from the photo document. */
function toCard(id, p) {
  return {
    id,
    ownerUid: p.ownerUid || '',
    thumbUrl: p.thumbUrl || '',
    // THE POST'S OTHER PICTURES. Easy to forget, and the only listing path that
    // enumerates fields instead of spreading the document — which is exactly
    // how a five-image product opened from search showed no carousel at all
    // while the same product opened from the feed did.
    blobIds: Array.isArray(p.blobIds) ? p.blobIds : null,
    title: p.title || '',
    description: p.description || '',
    type: p.type || '',
    subtype: p.subtype || '',
    color: p.color || '',
    price: typeof p.price === 'number' ? p.price : null,
    currency: p.currency || '',
    productUrl: p.productUrl || '',
    visible: p.visible !== false,
    createdAt: p.createdAt || null,
  };
}

/**
 * Listings of one type, newest first. `type` empty means "the newest of
 * anything".
 */
export async function fetchProducts(type = '', { force = false } = {}) {
  const key = type || '';
  const hit = _cache.get(key);
  if (!force && hit && Date.now() - hit.at < CACHE_TTL_MS) return hit.rows;
  if (!force && _inflight.has(key)) return _inflight.get(key);

  const req = (async () => {
    try {
      const clauses = [where('visible', '==', true)];
      if (key) clauses.push(where('type', '==', key));

      const run = (ordered) => getDocs(query(
        collection(db, 'photos'),
        ...clauses,
        // The ordering is what makes this need a composite index — two
        // equality filters alone Firestore can serve by merging its automatic
        // single-field indexes.
        ...(ordered ? [orderBy('createdAt', 'desc')] : []),
        limit(POOL),
      ));

      // ── IT WORKS WITHOUT A DEPLOY ────────────────────────────────────────
      // The ordered query is the one worth having: "newest of this type" is
      // what a catalogue should open on. But it needs a composite index, and
      // until that is deployed the whole Products tab returned nothing — a
      // feature switched off by an ops step nobody had reason to know about.
      //
      // So a refusal falls back to the SAME FILTER, unordered, which the
      // automatic indexes already serve. The rows are then sorted here. That
      // is not identical — the server picks which 60 without regard to age, so
      // this is 60 of that type rather than the 60 newest — but it is a real
      // result set instead of an empty page, and it needs nothing deployed.
      //
      // SELF-HEALING: the moment the index exists the first branch succeeds
      // and the ordering is exact again, with no code change.
      let snap;
      let ordered = true;
      try {
        snap = await run(true);
      } catch (err) {
        if (err?.code !== 'failed-precondition') throw err;
        // eslint-disable-next-line no-console
        console.warn('[products] no composite index yet — falling back to an '
          + 'unordered query. Deploy firestore.indexes.json for newest-first.');
        snap = await run(false);
        ordered = false;
      }

      const rows = snap.docs
        .map(d => toCard(d.id, d.data()))
        // A PRICED listing is a product; anything else in `photos` is a
        // designer's work, which has no business in a product search. Done
        // here rather than in the query because a second inequality would
        // force the ordering onto `price` and lose "newest first".
        .filter(p => typeof p.price === 'number' && p.price > 0);

      // Newest first among what came back, when the server could not do it.
      if (!ordered) {
        const ms = (v) => (typeof v?.toMillis === 'function' ? v.toMillis() : 0);
        rows.sort((a, b) => ms(b.createdAt) - ms(a.createdAt));
      }

      _cache.set(key, { rows, at: Date.now() });
      return rows;
    } catch (err) {
      // ── FAILURE IS NOT AN EMPTY CATALOGUE ────────────────────────────────
      // Anything that reaches here defeated the fallback too, so it is a real
      // failure — a dropped connection, a rules refusal — and it is rethrown
      // rather than returned as []. Swallowing it made every error look
      // identical to "no products match": a filter that appeared broken with
      // nothing anywhere saying why.
      //
      // NOT CACHED either, so a retry a moment later actually retries.
      throw err;
    } finally {
      _inflight.delete(key);
    }
  })();

  _inflight.set(key, req);
  return req;
}

/** Publishing or editing a listing makes the pools wrong; drop them all. */
export function invalidateProducts() {
  _cache.clear();
}

// Lowercased, accents stripped — the same normalisation the other two
// directories use, so "José" typed as "Jose" still matches.
// Lowercased, accents stripped, and TRANSLITERATED — so `ator` finds
// `աթոռ` and `стол` finds `stol`. Was a private copy of a
// lowercase-and-strip helper that each of the three directories kept its
// own identical version of; see utils/searchKey for what it does now and
// what it deliberately does not.
const norm = searchKey;

/**
 * Refines a pool in memory.
 *
 * @param rows      from fetchProducts()
 * @param q         free text — name, description, and the translated labels
 * @param filters   { subtype, color, minPrice, maxPrice }
 * @param label     (key) => translated label, so a search for "chandelier" in
 *                  any of the three locales hits the right listings
 */
export function filterProducts(rows, q, filters = {}, label = (k) => k) {
  const needle = norm(q);
  const { type = '', subtype = '', color = '', minPrice = '', maxPrice = '' } = filters;
  const min = minPrice === '' || minPrice == null ? null : Number(minPrice);
  const max = maxPrice === '' || maxPrice == null ? null : Number(maxPrice);

  return rows.filter(p => {
    // The type is normally applied by the query, but the cached pool for one
    // type is reused when the filter is cleared — so it is re-checked here and
    // costs nothing when it already holds.
    if (type && p.type !== type) return false;
    // A subtype only means anything under its own type.
    if (subtype && !(p.subtype === subtype && isSubtypeOf(p.type, subtype))) return false;
    if (color && p.color !== color) return false;

    if (min != null && Number.isFinite(min) && (p.price == null || p.price < min)) return false;
    if (max != null && Number.isFinite(max) && (p.price == null || p.price > max)) return false;

    if (needle) {
      const hay = [
        p.title,
        p.description,
        // KEYS AND LABELS BOTH. "chandelier" should find a listing whose
        // subtype is stored as `chandelier`, whatever language the shop
        // published in and whatever language the reader is using.
        p.type, p.subtype, p.color,
        p.type && label(`shop_cat_${p.type}`),
        p.subtype && label(`prod_sub_${p.subtype}`),
        p.color && label(`prod_color_${p.color}`),
      ].filter(Boolean).map(norm).join(' ');
      if (!hay.includes(needle)) return false;
    }

    return true;
  });
}
